import { Component, EventEmitter, Output, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { Subscription } from 'rxjs';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MaterialModule } from '../../../../../material/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService } from '../../../../../services/chat.service';
import { AuthService } from '../../../../../services/auth.service';
import { ChatMessage, ChatConversation } from '../../../../../models/chat-message.model';
import { environment } from '../../../../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { MissionDetailsComponent } from '../../../agency-etab-missions-details/encour-component/mission-details/mission-details.component';
import { FlagService } from '../../../../../shared/services/flag.service';
import { FlagType } from '../../../../../shared/utils/enums';
import { PermissionService } from '../../../../../services/permission.service';
import { AgencyPermission } from '../../../../../shared/utils/agency-rbac';

@Component({
  selector: 'app-chat-popup',
  templateUrl: './chat-popup.component.html',
  styleUrls: ['./chat-popup.component.scss'],
  standalone: true,
  imports: [AngularSvgIconModule, MaterialModule, CommonModule, FormsModule, MissionDetailsComponent],
})
export class ChatPopupComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Output() closeChat = new EventEmitter<void>();
  @ViewChild('messageContainer') private messageContainer!: ElementRef;
  @ViewChild('fileInput') private fileInput!: ElementRef;
  @ViewChild('textareaInput') private textareaInput!: ElementRef;

  view: 'list' | 'chat' = 'list';
  conversations: ChatConversation[] = [];
  filteredConversations: ChatConversation[] = [];
  searchFilter: string = '';
  selectedConversation: ChatConversation | null = null;

  messages: ChatMessage[] = [];
  newMessage: string = '';
  currentUserId: number | null = null;
  agencyId: number | null = null;
  private shouldScrollToBottom = false;
  private messagesSubscription: Subscription | null = null;
  private unreadCountSubscription: Subscription | null = null;
  private globalMessagesSubscription: Subscription | null = null; // Global subscription for sound alerts
  private conversationOpen = false; // Track if conversation is currently open
  private userSubscription: Subscription | null = null;
  private initialized = false;

  // UI State
  isExpanded = false;

  // Mission Details Split View
  showMissionDetails = false;
  selectedMissionData: { id: number; titre: string } | null = null;

  // Voice recording
  isRecording = false;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];
  recordingDuration = 0;
  private recordingInterval: any;
  private conversationRefreshInterval: any;

  // File attachment
  selectedFile: File | null = null;
  readonly maxFileSizeMo = 5;

  constructor(
    private chatService: ChatService,
    private authService: AuthService,
    private dialog: MatDialog,
    private flag: FlagService,
    private permissions: PermissionService
  ) {}

  ngOnInit(): void {
    // Get current user info. The user stream also re-emits when permissions are refreshed:
    // the sound subscription and the polling interval must be created only once.
    this.userSubscription = this.authService.getUser().subscribe(user => {
      if (user) {
        // The connected collaborator (traceability) vs the agency it acts for (shared inbox).
        // Every agency-side message is sent with senderId = agencyId — like the legacy agency
        // account and system messages — so read/unread tracking (senderId != reader) stays shared
        // by the whole agency; the real author travels in senderUserId.
        this.currentUserId = user.id;
        this.agencyId = this.authService.agencyId;

        // Sound filtering: messages from the agency side are "ours".
        this.chatService.setCurrentUserId(this.agencyId!);

        if (this.agencyId && !this.initialized) {
          this.initialized = true;
          this.loadConversations();

          // Subscribe to sound notifications from chat service
          this.globalMessagesSubscription = this.chatService.soundNotification$.subscribe(() => {
            this.chatService.playMessageSound();
          });

          // Refresh conversation list every 5 seconds to catch new messages
          this.conversationRefreshInterval = setInterval(() => {
            if (this.view === 'list') {
              this.loadConversations();
            }
          }, 5000);
        }
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
    if (this.unreadCountSubscription) {
      this.unreadCountSubscription.unsubscribe();
    }
    if (this.globalMessagesSubscription) {
      this.globalMessagesSubscription.unsubscribe();
    }
    this.chatService.disconnect();
    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
    }
    if (this.conversationRefreshInterval) {
      clearInterval(this.conversationRefreshInterval);
    }
  }

  toggleExpand(): void {
    this.showMissionDetails = false;
    this.selectedMissionData = null;
    this.isExpanded = !this.isExpanded;
  }

  loadConversations(): void {
    if (this.agencyId) {
      this.chatService.getConversations(this.agencyId).subscribe({
        next: (conversations) => {
          this.conversations = conversations;
          this.applyFilter();
          if (this.conversations.length === 1 && this.view === 'list') {
            this.selectConversation(this.conversations[0]);
          }
        },
        error: (error) => {
          console.error('Error loading conversations:', error);
        }
      });
    }
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  applyFilter(): void {
    if (!this.searchFilter.trim()) {
      this.filteredConversations = [...this.conversations];
    } else {
      const filterLower = this.searchFilter.toLowerCase().trim();
      this.filteredConversations = this.conversations.filter(conv =>
        conv.candidatName.toLowerCase().includes(filterLower)
      );
    }
  }

  clearSearch(): void {
    this.searchFilter = '';
    this.applyFilter();
  }

  selectConversation(conversation: ChatConversation): void {
    // If conversation has unread messages, notify layout to reduce global counter
    if (conversation.unreadCount > 0) {
      this.chatService.notifyChatOpen(conversation.unreadCount);
    }

    this.selectedConversation = conversation;
    this.view = 'chat';
    this.messages = [];
    this.conversationOpen = true; // Mark conversation as opened FIRST

    // Update conversation's unread count immediately (before any WebSocket updates)
    conversation.unreadCount = 0;

    if (this.agencyId && this.currentUserId) {
      // Connect to WebSocket first to enable auto-read on open
      this.chatService.connect(conversation.candidatId, this.agencyId);

      // Notify server that conversation is opened - triggers auto-read (reader = the agency)
      this.chatService.notifyConversationOpen(
        conversation.candidatId,
        this.agencyId,
        this.agencyId,
        'AGENCY'
      );

      // Mark messages as read via REST API as well for immediate update
      this.chatService.markMessagesAsRead(conversation.candidatId, this.agencyId, this.agencyId).subscribe({
        next: () => {
          // Update the conversation's unread count locally
          conversation.unreadCount = 0;
        },
        error: (error) => {
          console.error('Error marking messages as read:', error);
        }
      });

      // Load history
      this.chatService.getConversationHistory(conversation.candidatId, this.agencyId).subscribe({
        next: (messages) => {
          this.messages = messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          // Ensure scroll happens after DOM updates
          setTimeout(() => {
            this.shouldScrollToBottom = true;
          }, 100);
        }
      });

      // Subscribe to real-time messages
      if (this.messagesSubscription) {
        this.messagesSubscription.unsubscribe();
      }
      this.messagesSubscription = this.chatService.messages$.subscribe((message: ChatMessage) => {
        // Only add if it belongs to this conversation
        if (message.candidatId === this.selectedConversation?.candidatId) {
          // If conversation is open, mark incoming messages as read immediately
          if (this.conversationOpen) {
            message.isRead = true;
            // Also mark in database immediately via WebSocket notification
            this.chatService.notifyConversationOpen(
              message.candidatId,
              this.agencyId!!,
              this.agencyId!!,
              'AGENCY'
            );
          }
          this.messages.push(message);
          this.shouldScrollToBottom = true;
        }
      });

      // Subscribe to unread count updates from WebSocket - only process if conversation is NOT open
      if (this.unreadCountSubscription) {
        this.unreadCountSubscription.unsubscribe();
      }
      this.unreadCountSubscription = this.chatService.unreadCount$.subscribe((update) => {
        if (update.candidatId === conversation.candidatId && update.agencyId === this.agencyId) {
          // If conversation is open, don't update the unread count
          if (!this.conversationOpen) {
            conversation.unreadCount = update.unreadCount;
          }
        }
      });
    }
  }

  backToList(): void {
    // Mark conversation as closed so incoming messages count as unread
    //this.conversationOpen = false;

    // Notify that chat is closed
    //this.chatService.notifyChatClose();

    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
      this.messagesSubscription = null;
    }
    this.chatService.disconnect();
    this.selectedConversation = null;
    this.view = 'list';
    this.loadConversations(); // Refresh to update last message/unread count
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'video/mp4', 'video/webm', 'video/ogg'];
      if (!validTypes.includes(file.type)) {
        this.flag.displayFlag('Seuls les fichiers PNG, JPEG, JPG, PDF et vidéos sont autorisés.', FlagType.error);
        this.fileInput.nativeElement.value = '';
        return;
      }

      // Validate file size (max maxFileSizeMo Mo)
      if (file.size > this.maxFileSizeMo * 1024 * 1024) {
        this.flag.displayFlag(`La taille du fichier ne doit pas dépasser ${this.maxFileSizeMo} Mo.`, FlagType.error);
        this.fileInput.nativeElement.value = '';
        return;
      }

      this.selectedFile = file;
      this.sendFileAttachment();
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  sendFileAttachment(): void {
    if (!this.selectedFile || !this.currentUserId || !this.selectedConversation || !this.agencyId) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];

      let attachmentType: 'IMAGE' | 'PDF' | 'VIDEO' = 'IMAGE';
      if (this.selectedFile!.type === 'application/pdf') {
        attachmentType = 'PDF';
      } else if (this.selectedFile!.type.startsWith('video/')) {
        attachmentType = 'VIDEO';
      }

      const message: any = {
        senderId: this.agencyId,
        senderUserId: this.currentUserId,
        senderType: 'AGENCY',
        senderName: 'Collary',
        candidatId: this.selectedConversation!.candidatId,
        agencyId: this.agencyId,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: attachmentType,
        attachmentPath: base64Data,
        attachmentFileName: this.selectedFile!.name
      };

      this.chatService.sendMessage(message);
      this.selectedFile = null;
      this.fileInput.nativeElement.value = '';
    };

    reader.readAsDataURL(this.selectedFile);
  }

  async startRecording(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      this.recordingDuration = 0;

      // Start recording timer
      this.recordingInterval = setInterval(() => {
        this.recordingDuration++;
      }, 1000);

      this.mediaRecorder.ondataavailable = (event) => {
        this.audioChunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.sendVoiceMessage(audioBlob);
        stream.getTracks().forEach(track => track.stop());

        // Clear timer
        if (this.recordingInterval) {
          clearInterval(this.recordingInterval);
          this.recordingInterval = null;
        }
      };

      this.mediaRecorder.start();
      this.isRecording = true;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Impossible d\'accéder au microphone');
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  sendVoiceMessage(audioBlob: Blob): void {
    if (!this.currentUserId || !this.selectedConversation || !this.agencyId) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64Data = (reader.result as string).split(',')[1];

      const message: any = {
        senderId: this.agencyId,
        senderUserId: this.currentUserId,
        senderType: 'AGENCY',
        senderName: 'Collary',
        candidatId: this.selectedConversation!.candidatId,
        agencyId: this.agencyId,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: 'VOICE',
        attachmentPath: base64Data,
        attachmentFileName: `voice_${Date.now()}.webm`
      };

      this.chatService.sendMessage(message);
    };

    reader.readAsDataURL(audioBlob);
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.currentUserId || !this.selectedConversation || !this.agencyId) {
      return;
    }

    const message: ChatMessage = {
      senderId: this.agencyId,
      senderUserId: this.currentUserId,
      senderType: 'AGENCY',
      senderName: 'Collary',
      candidatId: this.selectedConversation.candidatId,
      agencyId: this.agencyId,
      message: this.newMessage.trim(),
      timestamp: new Date(),
      isRead: false
    };

    this.chatService.sendMessage(message);
    this.newMessage = '';
    this.resetTextareaHeight();
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    } else {
      // Auto-expand textarea on input
      setTimeout(() => this.autoExpandTextarea(), 0);
    }
  }

  autoExpandTextarea(): void {
    if (this.textareaInput && this.textareaInput.nativeElement) {
      const textarea = this.textareaInput.nativeElement;
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 192); // 8 lines max (24px line-height * 8 = 192px)
      textarea.style.height = newHeight + 'px';
    }
  }

  resetTextareaHeight(): void {
    if (this.textareaInput && this.textareaInput.nativeElement) {
      const textarea = this.textareaInput.nativeElement;
      textarea.style.height = 'unset';
    }
  }

  /** Agency-side messages (any collaborator) are shown on "our" side of the conversation. */
  isMyMessage(message: ChatMessage): boolean {
    return message.senderType === 'AGENCY';
  }

  /** Roles without CANDIDATE_CONTACT (e.g. secrétaire) can read conversations but not write. */
  get canContact(): boolean {
    return this.permissions.has(AgencyPermission.CANDIDATE_CONTACT);
  }

  getAttachmentUrl(message: ChatMessage): string {
    return `${environment.api.BASE_URL}${message.attachmentPath}`;
  }

  toggleVoicePlay(message: any): void {
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach((audio: HTMLAudioElement) => {
      if (audio.src === this.getAttachmentUrl(message)) {
        if (audio.paused) {
          // Pause all other audio
          audioElements.forEach(a => {
            if (a !== audio) {
              a.pause();
              a.currentTime = 0;
            }
          });

          // Update all messages to not playing
          this.messages.forEach(m => (m as any).isPlaying = false);

          // Play this audio
          audio.play();
          (message as any).isPlaying = true;
        } else {
          audio.pause();
          (message as any).isPlaying = false;
        }
      }
    });
  }

  onVoiceEnded(message: any): void {
    (message as any).isPlaying = false;
  }

  openMissionDetails(message: ChatMessage): void {
    this.showMissionDetails = false;
    this.selectedMissionData = null;
    setTimeout(() => {
      if (message.missionId) {
        this.selectedMissionData = {
          id: message.missionId,
          titre: message.missionRef || 'Mission'
        };
        this.showMissionDetails = true;
        // Expand the chat popup to show both views
        this.isExpanded = true;
      }
    }, 0);
  }

  closeMissionDetails(): void {
    this.showMissionDetails = false;
    this.selectedMissionData = null;
    // Collapse back to normal chat size
    this.isExpanded = false;
  }

  private scrollToBottom(): void {
    try {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }
}
